// UpayMargTab.tsx
import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Compass } from "lucide-react";
import AstriaIndiaV2Shell, { SHELL_COLORS_INDIA_V2 } from "../AstriaIndiaV2Shell";
import {
  AstriaIndiaV2ResultCard,
  AstriaIndiaV2FieldBlock,
  AstriaIndiaV2Divider,
  AstriaIndiaV2HighlightBadge,
  AstriaIndiaV2SignatureLine,
  IndiaV2LoadingState,
  IndiaV2ErrorState,
  IndiaV2ChatCta,
} from "../AstriaIndiaV2Card";
import {
  WizardProgress,
  WizardStep,
  WizardNav,
  WizardChips,
  WizardSlider,
} from "../AstriaIndiaV2Wizard";
import { useAstriaIndiaV2Tab } from "../useAstriaIndiaV2Tab";
import { AstriaIndiaV2UpayData } from "../../../../contexts/ChatContext";
import { useIndiaV2Language } from "../../../../utils/India/astriaIndiaV2Language";
import { ASTRIA_INDIA_V2_COPY } from "../../../../utils/India/astriaIndiaV2Copy";
import { useIndiaV2Copy } from "../../../../utils/India/useIndiaV2Copy";

const SUBCATEGORY_NAME = "Upay Marg";

const TOTAL_STEPS = 3;

const UpayMargTab: React.FC = () => {
  // Which engine to call is decided ONLY by which sidebar entry the user
  // clicked ("Astria India V2" vs "Astria India V3" — see
  // Sidebar.tsx/TesterHome.tsx's "?v=3" query flag) and never changes for
  // the lifetime of this page — no in-page switch.
  const [searchParams] = useSearchParams();
  const version: "v2" | "v3" = searchParams.get("v") === "3" ? "v3" : "v2";
  const categoryName = version === "v3" ? "Astria India V3" : "Astria India V2";

  const lang = useIndiaV2Language();
  const copy = useIndiaV2Copy(lang);
  const t = copy.upay;
  const shared = copy.shared;

  const { loading, error, message, send, reset, goToChat } = useAstriaIndiaV2Tab({
    categoryName,
    subCategoryName: SUBCATEGORY_NAME,
  });

  const [step, setStep] = useState(1);
  const [situation, setSituation] = useState<string | null>(null);
  const [weight, setWeight] = useState(50);
  const [outcome, setOutcome] = useState<string | null>(null);

  const started = !!message || loading || !!error;

  const buildMessage = () => {
    const situationLabel =
      t.situationOptions.find((s) => s.value === situation)?.label || "a heavy feeling";
    const outcomeLabel = t.outcomeOptions.find((o) => o.value === outcome)?.label || "clarity";

    return `I'm feeling ${situationLabel.toLowerCase()}, and it feels about ${weight}/100 heavy right now. What I'm hoping for is ${outcomeLabel.toLowerCase()}. Please offer me an Upay Marg reading.`;
  };

  const handleSubmit = () => {
    send(buildMessage());
  };

  const upayData = message?.astriaIndiaV2Data as AstriaIndiaV2UpayData | undefined;

  if (started) {
    return (
      <AstriaIndiaV2Shell
        active="upay"
        lang={lang}
       
        version={version}
      >
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: "#2E1F45", marginBottom: 4 }}>
            {t.tabLabel}
          </div>
          <p style={{ fontSize: 13.5, color: "#8C7DA0" }}>
            {t.tabSublabel} — {t.resultTitle}
          </p>
        </div>

        {loading && <IndiaV2LoadingState label={t.loading} />}

        {!loading && error && (
          <IndiaV2ErrorState message={error} onRetry={handleSubmit} retryLabel={shared.tryAgain} />
        )}

        {!loading && !error && upayData && (
          <>
            <AstriaIndiaV2ResultCard
              icon={<Compass className="w-5 h-5" />}
              title={t.resultTitle}
              subtitle={t.tabLabel}
              accent={["#2E8B8B", "#6B3FA0"]}
              shareText={`${t.tabLabel}\n\n${upayData.current_energy}\n\n${upayData.gentle_closing}`}
              shareLabels={shared}
            >
              <AstriaIndiaV2HighlightBadge text={upayData.highlight} />
              <AstriaIndiaV2FieldBlock label={t.fields.currentEnergy} value={upayData.current_energy} />
              <AstriaIndiaV2FieldBlock label={t.fields.vedicReflection} value={upayData.vedic_reflection} />
              <AstriaIndiaV2SignatureLine value={upayData.micro_imagery} />
              <AstriaIndiaV2SignatureLine value={upayData.clarity_point} emphasis />

              {upayData.suggested_upay?.length > 0 && (
                <>
                  <div
                    style={{
                      fontSize: 10.5,
                      fontWeight: 700,
                      letterSpacing: "0.09em",
                      textTransform: "uppercase",
                      color: SHELL_COLORS_INDIA_V2.accent,
                      marginBottom: 10,
                    }}
                  >
                    {t.fields.suggestedPractices}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 6 }}>
                    {upayData.suggested_upay.map((u, i) => (
                      <div
                        key={i}
                        style={{
                          padding: "14px 16px",
                          borderRadius: 14,
                          background: SHELL_COLORS_INDIA_V2.section,
                          border: `1px solid ${SHELL_COLORS_INDIA_V2.cardBorder}`,
                        }}
                      >
                        <div style={{ fontSize: 14.5, fontWeight: 700, color: "#2E1F45", marginBottom: 4 }}>
                          {u.title}
                        </div>
                        <div style={{ fontSize: 13.5, color: "#4A3B5C", lineHeight: 1.65 }}>
                          {u.description}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              <AstriaIndiaV2Divider />
              <p style={{ fontSize: 14, fontStyle: "italic", color: "#4A3B5C" }}>
                {upayData.gentle_closing}
              </p>
            </AstriaIndiaV2ResultCard>
            <button
              onClick={() => {
                reset();
                setStep(1);
              }}
              className="text-xs font-semibold transition-colors"
              style={{ marginTop: 14, color: "#8C7DA0" }}
            >
              {shared.startNewReading}
            </button>
            <IndiaV2ChatCta onClick={goToChat} label={shared.continueInChat} />
          </>
        )}

        {!loading && !error && !upayData && message && (
          <AstriaIndiaV2ResultCard
            icon={<Compass className="w-5 h-5" />}
            title={t.resultTitle}
            subtitle={t.tabLabel}
            accent={["#2E8B8B", "#6B3FA0"]}
          >
            <p style={{ fontSize: 15, lineHeight: 1.75, color: "#4A3B5C", whiteSpace: "pre-wrap" }}>
              {message.content}
            </p>
          </AstriaIndiaV2ResultCard>
        )}
      </AstriaIndiaV2Shell>
    );
  }

  return (
    <AstriaIndiaV2Shell
      active="upay"
      lang={lang}
     
      version={version}
    >
      <WizardProgress
        step={step}
        total={TOTAL_STEPS}
        accent="#2E8B8B"
        stepLabel={shared.stepOf}
        percentLabel={shared.percentComplete}
      />

      {step === 1 && (
        <WizardStep stepKey="situation" question={t.step1Question}>
          <WizardChips options={t.situationOptions} value={situation} onChange={setSituation} />
          <WizardNav
            showBack={false}
            nextDisabled={!situation}
            onNext={() => setStep(2)}
            nextLabel={shared.continueLabel}
            backLabel={shared.back}
          />
        </WizardStep>
      )}

      {step === 2 && (
        <WizardStep stepKey="weight" question={t.step2Question}>
          <WizardSlider value={weight} onChange={setWeight} lowLabel={t.weightLow} highLabel={t.weightHigh} />
          <WizardNav
            onBack={() => setStep(1)}
            onNext={() => setStep(3)}
            nextLabel={shared.continueLabel}
            backLabel={shared.back}
          />
        </WizardStep>
      )}

      {step === 3 && (
        <WizardStep stepKey="outcome" question={t.step3Question}>
          <WizardChips options={t.outcomeOptions} value={outcome} onChange={setOutcome} />
          <WizardNav
            onBack={() => setStep(2)}
            nextLabel={t.revealButton}
            nextDisabled={!outcome}
            onNext={handleSubmit}
            backLabel={shared.back}
          />
        </WizardStep>
      )}
    </AstriaIndiaV2Shell>
  );
};

export default UpayMargTab;
